import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AuthServiceLocal } from 'src/@order/services/auth.service';
import { OrderData } from '../get-qr-order-details/get-qr-order-details.component';

@Component({
  selector: 'app-specific-order-tracking',
  templateUrl: './specific-order-tracking.component.html',
  styleUrls: ['./specific-order-tracking.component.scss']
})
export class SpecificOrderTrackingComponent implements OnInit {
  private destroy$ = new Subject<void>();

  data: OrderData = {
    weightClass: '',
    deliveryPlan: '',
    securityCover: '',
    phlenuTrustCheck: '',
    packageContent: '',
    orderId: '',
    orderStatus: '',
  };

  orderDetail: any = {};
  dropDetail: any = {};
  orderId: string = '';
  token: string = '';
  isLoading: boolean = false;
  hasError: boolean = false;
  errorMessage: string = '';
  isUpdatingStatus: boolean = false;

  // Status options for dropdown
  statusOptions = [
    { value: 'processing', label: 'Processing' },
    { value: 'in_transit', label: 'In Transit' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'canceled', label: 'Canceled' },
    { value: 'recipient_re_scheduled', label: 'Recipient Re-Scheduled' },
    { value: 'held_at_distribution_site', label: 'Held at Distribution Site' },
    { value: 'returned', label: 'Returned' },
  ];

  selectedStatus: string = '';
  remarks: string = '';

  // Helper function to get currency symbol based on country
  getCurrencySymbol(country: string): string {
    switch (country?.toLowerCase()) {
      case 'nigeria':
        return '₦';
      case 'ghana':
        return '₵';
      default:
        return '₵'; // Default to Ghanaian Cedi
    }
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthServiceLocal,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    // Get order ID from route parameters and token from query parameters
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((queryParams) => {
        this.token = queryParams['token'] || '';

        console.log('token', this.token);

        if (this.token) {
          this.loadOrderData();
        } else {
          this.hasError = true;
          this.errorMessage = 'Token is required';
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public loadOrderData(): void {
    if (!this.token) {
      this.hasError = true;
      this.errorMessage = 'Token is required';
      return;
    }

    this.isLoading = true;
    this.hasError = false;
    this.errorMessage = '';

    this.authService
      .getDsOrderDetailsDropTracking({ token: this.token })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response && response.response === 1) {
            this.mapApiResponseToData(response.data);
          } else {
            this.hasError = true;
            this.errorMessage =
              response?.message || 'Failed to load order details';
            this.snackBar.open(this.errorMessage, 'Close', { duration: 5000 });
          }
        },
        error: (error) => {
          this.isLoading = false;
          this.hasError = true;
          this.errorMessage = 'Error loading order details. Please try again.';
          console.error('API Error:', error);
          this.snackBar.open(this.errorMessage, 'Close', { duration: 5000 });
        },
      });
  }

  private mapApiResponseToData(apiData: any): void {
    // Map API response to component data structure
    const orderDetail = apiData.orderDetail;
    const dropDetail = apiData.dropDetail;

    // Set the raw data for template access
    this.orderDetail = orderDetail || {};
    this.dropDetail = Array.isArray(dropDetail) ? dropDetail : [dropDetail || {}];

    // Map order status from numeric to string
    const getStatusString = (status: number): string => {
      switch (status) {
        case 1:
          return 'processing';
        case 2:
          return 'in_transit';
        case 3:
          return 'delivered';
        case 4:
          return 'canceled';
        case 5:
          return 'recipient_re_scheduled';
        case 6:
          return 'held_at_distribution_site';
        case 7:
          return 'returned';
        default:
          return 'processing';
      }
    };

    // Map delivery plan from numeric to string
    const getDeliveryPlanString = (plan: number): string => {
      switch (plan) {
        case 1:
          return 'Express';
        case 2:
          return 'Regular';
        case 3:
          return 'Saver';
        default:
          return 'Regular';
      }
    };

    // Map package size from numeric to string
    const getPackageSizeString = (size: number): string => {
      switch (size) {
        case 1:
          return 'Small';
        case 2:
          return 'Medium';
        case 3:
          return 'Large';
        case 4:
          return 'Extra Large';
        default:
          return 'Medium';
      }
    };

    console.log('orderDetail', orderDetail);
    console.log('dropDetail', this.dropDetail);

    const firstDropDetail = this.dropDetail[0] || {};

    this.data = {
      orderId: orderDetail?.id?.toString() || '',
      weightClass:
        getPackageSizeString(firstDropDetail?.packageSize) || 'Not Available',
      deliveryPlan:
        getDeliveryPlanString(firstDropDetail?.deliveryPlan) || 'Not Available',
      securityCover: firstDropDetail?.isCoverApplied ? 'Applied' : 'Not Applied',
      phlenuTrustCheck: firstDropDetail?.isPhlenuTrustCheckApplied
        ? 'Applied'
        : 'Not Applied',
      packageContent: firstDropDetail?.sendingItemName || 'Not Available',
      orderStatus: getStatusString(firstDropDetail?.dropStatus) || 'processing',
    };

    // Set the selected status for the dropdown
    this.selectedStatus = this.data.orderStatus;
    this.orderId = orderDetail?.id || '';
  }
}